/* relix-attr.js v2.6 (2026-11-02)
 * Captures ad click IDs, UTMs, Google/Meta extras, referrer and landing page.
 * Last touch: overwritten on each new tagged or externally referred landing,
 *   all keys rewritten (missing ones blanked) so sources never mix.
 * First touch: written once, never overwritten.
 * Also stores the GA4 client id (from _ga) so GA4 behavior joins to orders.
 * The 9 v1 attribute names are unchanged, so the current orders loader keeps working.
 * v2.1: when a parameter repeats in the URL, the LAST non-empty value wins.
 *   Meta catalog ads append the ad-level URL parameters after the feed's own
 *   utm_source=facebook tags, so last-wins keeps {{site_source_name}} (ig/fb).
 * v2.2: sends GA4 event rlx_touch (once per new touch) and user properties
 *   ft_channel_group / lt_channel_group. Labels only, no click IDs, UTMs or PII.
 *   ROLLBACK: restore v2.1 (remove channelGroup, clickType, sendTouchToGA4 and the 3 marked init lines).
 * v2.3: on Klaviyo form submit, writes rlx_signup_* properties (channel, click type,
 *   Meta campaign/ad set/ad ids, touch time) to the Klaviyo profile and sends GA4 event
 *   rlx_signup. Once per browser per 90 days (_relix_kl_sig cookie).
 *   ROLLBACK: delete the block marked v2.3 (SIGNUP_COOKIE through the klaviyoForms listener).
 * v2.4: also listens for Alia's document event alia:signup (the relix.shop popup is Alia,
 *   not a Klaviyo form, so klaviyoForms never fires for it). Same once-per-browser guard.
 * v2.6: also stamps rlx_signup_ua, rlx_signup_fbp and rlx_signup_fbc on the profile so the
 *   server-side Conversions API Lead (n8n) gets full Meta matching. fbc comes from Meta's _fbc
 *   cookie, or is built from the stored fbclid when that cookie is missing. (v2.5 was dropped.)
 */
(function () {
  'use strict';

  var LT_COOKIE = '_relix_attr'; // same name as v1 so existing visitors carry over
  var FT_COOKIE = '_relix_ft';
  var COOKIE_DAYS = 90;

  var URL_KEYS = [
    'gclid', 'gbraid', 'wbraid', 'fbclid',
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'gad_campaignid', 'gad_source', 'srsltid',
    'rlx_net', 'rlx_pl'
  ];
  var META_KEYS = ['rlx_ref', 'rlx_lp', 'rlx_ts'];
  var LT_KEYS = URL_KEYS.concat(META_KEYS);

  // Hosts that are not a marketing source (own store, checkout, payment hops)
  var INTERNAL_HOST = /(^|\.)(relix\.shop|myshopify\.com|shopify\.com|shop\.app|relixmarketplace\.com|paypal\.com|afterpay\.com|klarna\.com|affirm\.com)$/i;

  // consent
  function consentAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp) return true;
      return cp.analyticsProcessingAllowed() || cp.marketingAllowed();
    } catch (e) { return true; }
  }

  // cookies
  function readCookie(name) {
    try {
      var m = document.cookie.match('(?:^|;)\\s*' + name + '=([^;]*)');
      return m ? JSON.parse(decodeURIComponent(m[1])) : null;
    } catch (e) { return null; }
  }
  function writeCookie(name, data) {
    try {
      var exp = new Date(Date.now() + COOKIE_DAYS * 864e5).toUTCString();
      document.cookie = name + '=' + encodeURIComponent(JSON.stringify(data)) +
        '; path=/; expires=' + exp + '; SameSite=Lax; Secure';
    } catch (e) {}
  }
  function gaClientId() {
    try {
      var m = document.cookie.match(/(?:^|;)\s*_ga=([^;]*)/);
      if (!m) return '';
      var p = decodeURIComponent(m[1]).split('.');
      return p.length >= 4 ? p[p.length - 2] + '.' + p[p.length - 1] : '';
    } catch (e) { return ''; }
  }

  // v2.2: GA4 touch context (labels only)
  var META_SRC = /^(facebook|fb|instagram|ig|meta|an|msg|threads)$/;
  function channelGroup(t) {
    if (!t) return 'direct';
    var src = String(t.utm_source || '').toLowerCase();
    var med = String(t.utm_medium || '').toLowerCase();
    var ref = String(t.rlx_ref || '').toLowerCase();
    if (t.gclid || t.gbraid || t.wbraid || t.gad_source || t.gad_campaignid) return 'paid_google';
    if (META_SRC.test(src) && /paid|cpc/.test(med)) return 'paid_meta';
    if (/klaviyo/.test(src)) return /sms/.test(med) ? 'sms_klaviyo' : 'email_klaviyo';
    if (/beehiiv|relix.?daily/.test(src) || /newsletter\.relix\.com$/.test(ref)) return 'email_beehiiv';
    if (t.srsltid) return 'organic_shopping';
    if (/(^|\.)(google|bing|duckduckgo|yahoo|ecosia)\./.test(ref)) return 'organic_search';
    if (t.fbclid || /(facebook|instagram|threads|reddit|youtube)\.com$|^t\.co$/.test(ref)) return 'organic_social';
    if (/(^|\.)(relix\.com|jambands\.com)$/.test(ref)) return 'owned_media';
    if (ref) return 'referral';
    if (src) return 'other_tagged';
    return 'direct';
  }
  function clickType(t) {
    if (!t) return 'none';
    if (t.gclid || t.gbraid || t.wbraid) return 'google';
    if (t.fbclid) return 'meta';
    return 'none';
  }
  function sendTouchToGA4(touch, isFirst) {
    try {
      if (typeof window.gtag !== 'function') return;
      var grp = channelGroup(touch);
      window.gtag('set', 'user_properties', {
        ft_channel_group: channelGroup(readCookie(FT_COOKIE)),
        lt_channel_group: grp
      });
      window.gtag('event', 'rlx_touch', {
        touch_channel_group: grp,
        touch_click_type: clickType(touch),
        touch_placement: String((touch && touch.rlx_pl) || '(none)').slice(0, 40),
        touch_is_first: isFirst ? 'true' : 'false'
      });
    } catch (e) {}
  }

  // touch detection
  function paramsFromURL() {
    var sp = new URLSearchParams(window.location.search);
    var found = {};
    URL_KEYS.forEach(function (k) {
      // last non-empty occurrence wins (ad-level params override feed params)
      var all = sp.getAll(k).filter(function (v) { return v; });
      if (all.length) found[k] = all[all.length - 1].slice(0, 255);
    });
    return found;
  }
  function referrerHost() {
    try { return document.referrer ? new URL(document.referrer).hostname.toLowerCase() : ''; }
    catch (e) { return ''; }
  }
  function isExternal(host) {
    return !!host && host !== window.location.hostname.toLowerCase() && !INTERNAL_HOST.test(host);
  }

  // Returns { touch, isNew }. A new touch = tagged URL or external referrer.
  // Direct visits (no params, no external referrer) keep the stored last touch.
  function resolveTouch() {
    var params = paramsFromURL();
    var ref = referrerHost();
    var ext = isExternal(ref);
    if (Object.keys(params).length || ext) {
      var t = params;
      t.rlx_ref = ext ? ref : '';
      t.rlx_lp = window.location.pathname.slice(0, 200);
      t.rlx_ts = new Date().toISOString();
      return { touch: t, isNew: true };
    }
    return { touch: readCookie(LT_COOKIE), isNew: false };
  }

  // cart attributes
  function buildAttrs() {
    var lt = readCookie(LT_COOKIE);
    var ft = readCookie(FT_COOKIE);
    var attrs = {};
    if (lt) {
      LT_KEYS.forEach(function (k) { attrs[k] = lt[k] ? String(lt[k]) : ''; });
    }
    if (ft) attrs.rlx_ft = JSON.stringify(ft).slice(0, 1000);
    var cid = gaClientId();
    if (cid) attrs.rlx_ga_cid = cid;
    return attrs;
  }
  function differs(cartAttrs, attrs) {
    cartAttrs = cartAttrs || {};
    return Object.keys(attrs).some(function (k) {
      return (cartAttrs[k] || '') !== (attrs[k] || '');
    });
  }
  function syncCart() {
    var attrs = buildAttrs();
    if (!Object.keys(attrs).length) return;
    _origFetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!differs(cart.attributes, attrs)) return;
        return _origFetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attrs })
        });
      })
      .catch(function () {});
  }
  function pushAttrsNow() {
    var attrs = buildAttrs();
    if (!Object.keys(attrs).length) return;
    _origFetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).catch(function () {});
  }

  // add-to-cart intercepts (fetch and XHR)
  var _origFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (url.indexOf('/cart/add') !== -1) pushAttrsNow();
    } catch (e) {}
    return _origFetch.apply(this, arguments);
  };
  var _origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._relixUrl = url;
    return _origOpen.apply(this, arguments);
  };
  var _origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    try {
      if (this._relixUrl && String(this._relixUrl).indexOf('/cart/add') !== -1) pushAttrsNow();
    } catch (e) {}
    return _origSend.apply(this, arguments);
  };

  // ---- v2.3 start ----
  // Stamp signup attribution on the Klaviyo profile when a Klaviyo form is submitted.
  // Popup signups never reach checkout, so this is the only place their ad touch is recorded.
  // Labels and ad ids, plus Meta's fbc/fbp match keys (v2.6, used only for the Conversions API).
  // No gclid. Once per browser per 90 days so a later
  // form (or step 2 of a multi-step form) never overwrites the original signup source.
  var SIGNUP_COOKIE = '_relix_kl_sig';
  function rawCookie(name) {
    try {
      var m = document.cookie.match('(?:^|;)\\s*' + name + '=([^;]*)');
      return m ? decodeURIComponent(m[1]) : '';
    } catch (e) { return ''; }
  }
  function metaFbc(lt) {
    var c = rawCookie('_fbc');
    if (c) return c.slice(0, 500);
    if (lt && lt.fbclid) {
      var t = Date.parse(lt.rlx_ts || '') || Date.now();
      return ('fb.1.' + t + '.' + lt.fbclid).slice(0, 500);
    }
    return '';
  }
  function sendSignupAttribution(detail) {
    try {
      if (!consentAllowed()) return;
      if (readCookie(SIGNUP_COOKIE)) return;
      var ltRaw = readCookie(LT_COOKIE);
      var lt = ltRaw || {};
      var md = (detail && detail.metaData) || {};
      var props = {
        rlx_signup_at: new Date().toISOString(),
        rlx_signup_form_id: String((detail && detail.formId) || ''),
        rlx_signup_lt_channel: channelGroup(ltRaw),
        rlx_signup_ft_channel: channelGroup(readCookie(FT_COOKIE)),
        rlx_signup_click_type: clickType(ltRaw),
        rlx_signup_utm_source: lt.utm_source || '',
        rlx_signup_utm_medium: lt.utm_medium || '',
        rlx_signup_utm_campaign: lt.utm_campaign || '',
        rlx_signup_utm_term: lt.utm_term || '',
        rlx_signup_utm_content: lt.utm_content || '',
        rlx_signup_placement: lt.rlx_pl || '',
        rlx_signup_lt_ts: lt.rlx_ts || '',
        rlx_signup_lp: window.location.pathname.slice(0, 200),
        rlx_signup_ga_cid: gaClientId(),
        rlx_signup_ua: String(navigator.userAgent || '').slice(0, 400),
        rlx_signup_fbp: rawCookie('_fbp').slice(0, 200),
        rlx_signup_fbc: metaFbc(ltRaw)
      };
      var email = md.$email || md.email || '';
      if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
        var p = Object.assign({}, props);
        if (email) p.email = email;
        window.klaviyo.identify(p);
      } else {
        var q = Object.assign({}, props);
        if (email) q.$email = email;
        (window._learnq = window._learnq || []).push(['identify', q]);
      }
      writeCookie(SIGNUP_COOKIE, { ts: props.rlx_signup_at, f: props.rlx_signup_form_id });
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'rlx_signup', {
          signup_form_id: props.rlx_signup_form_id,
          touch_channel_group: props.rlx_signup_lt_channel,
          touch_click_type: props.rlx_signup_click_type
        });
      }
    } catch (e) {}
  }
  window.addEventListener('klaviyoForms', function (e) {
    if (e && e.detail && e.detail.type === 'submit') sendSignupAttribution(e.detail);
  });
  // v2.4: Alia popup signups (detail: { type: 'signup', popupID, email, phone, ... })
  document.addEventListener('alia:signup', function (e) {
    var d = (e && e.detail) || {};
    sendSignupAttribution({
      formId: 'alia:' + (d.popupID || ''),
      metaData: { email: d.email || '' }
    });
  });
  // ---- v2.3 end ----

  // main
  function init() {
    if (!consentAllowed()) {
      document.addEventListener('visitorConsentCollected', init, { once: true });
      return;
    }
    var r = resolveTouch();
    var isFirst = !readCookie(FT_COOKIE); // v2.2
    if (r.isNew) writeCookie(LT_COOKIE, r.touch);
    if (isFirst) {
      writeCookie(FT_COOKIE, r.isNew ? r.touch : {
        rlx_ref: '', rlx_lp: window.location.pathname.slice(0, 200), rlx_ts: new Date().toISOString()
      });
    }
    if (r.isNew || isFirst) sendTouchToGA4(r.isNew ? r.touch : null, isFirst); // v2.2
    syncCart();
    // _ga is set async by gtag; retry once so the client id lands on first pageview
    if (!gaClientId()) setTimeout(syncCart, 3000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
